import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogClose, DialogContent, Icon, toast } from "@sagot/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { dkKeys } from "../shared/keys";
import type { SourceUsage, SourceView } from "../shared/types";
import { useSourceVersions } from "./use-source-detail";

interface DeleteSourceDialogProps {
  source: SourceView;
  usage: SourceUsage | undefined;
  afterDeleteHref: string;
  onOpenChange: (open: boolean) => void;
}

/** `DELETE /v1/sources/{id}` then back to the Knowledge Base. */
export function DeleteSourceDialog({ source, usage, afterDeleteHref, onOpenChange }: DeleteSourceDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  const versions = useSourceVersions(source.id);
  const k = "deployKnowledge.sourceDetail.delete";
  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/sources/{source_id}", { params: { path: { source_id: source.id } } })),
    onSuccess: async () => {
      toast.success(t(`${k}.deleted`, { name: source.title }));
      queryClient.removeQueries({ queryKey: dkKeys.source(wsId, source.id) });
      await queryClient.invalidateQueries({ queryKey: dkKeys.sources(wsId), exact: true });
      await navigate(afterDeleteHref);
    },
    onError: (error) => toast.error(message(error)),
  });
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        title={t(`${k}.title`, { name: source.title })}
        description={t(`${k}.body`, {
          count: usage?.agents.length ?? 0,
          chunks: source.chunk_count,
          versions: versions.data?.length ?? source.version,
          citations: usage?.citations ?? 0,
        })}
        closeLabel={t("common.close")}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">{t("common.cancel")}</Button>
            </DialogClose>
            <Button variant="danger" loading={remove.isPending} onClick={() => { remove.mutate(); }}>
              <Icon name="trash" />
              {t(`${k}.confirm`)}
            </Button>
          </>
        }
      />
    </Dialog>
  );
}
